import type { CountryCode } from 'libphonenumber-js';
import { type ComponentProps, useState } from 'react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  callingCodeOf,
  countryOf,
  countryOptions,
  DEFAULT_COUNTRY,
  flagOf,
  formatForDisplay,
  toStorageValue,
} from '@/lib/utils/phone';

/**
 * Props the shared `FormControl` injects through Radix `Slot`. They must reach
 * a real control so the field shows and announces validation state.
 */
type FormControlInjectedProps = Pick<
  ComponentProps<typeof Input>,
  'aria-describedby' | 'aria-invalid' | 'aria-labelledby' | 'name'
>;

interface PhoneInputProps extends FormControlInjectedProps {
  disabled?: boolean;
  id?: string;
  /** Receives E.164 (`+15551234567`) when the number is complete, else the raw text. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** E.164 or legacy free text. */
  value: string;
}

const OPTIONS = countryOptions();

/**
 * Phone entry with a country selector.
 *
 * Stores E.164 so numbers are machine-usable, but displays them nationally —
 * `(555) 123-4567`, not `+15551234567`. The default country comes from
 * `VITE_DEFAULT_PHONE_COUNTRY` (falling back to `US`).
 *
 * Incomplete input is passed through rather than rejected; see
 * `toStorageValue` for why blocking would strand records that predate this
 * component.
 */
export function PhoneInput({
  id,
  value,
  onChange,
  placeholder,
  disabled = false,
  ...formControlProps
}: PhoneInputProps) {
  // Derived from the value on first render only: afterwards the user's explicit
  // choice wins, so re-deriving would fight them mid-edit.
  const [country, setCountry] = useState<CountryCode>(() => countryOf(value));

  const handleCountryChange = (next: string) => {
    const nextCountry = next as CountryCode;
    setCountry(nextCountry);
    // Re-interpret what is already typed against the new country.
    if (value) onChange(toStorageValue(formatForDisplay(value, country), nextCountry));
  };

  return (
    <div className={cn('flex gap-2')}>
      <Select value={country} disabled={disabled} onValueChange={handleCountryChange}>
        <SelectTrigger className="w-[7.5rem] shrink-0" aria-label="Country calling code">
          <SelectValue>
            <span className="flex items-center gap-1.5">
              <span aria-hidden>{flagOf(country)}</span>
              {callingCodeOf(country)}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {OPTIONS.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              <span aria-hidden className="mr-1.5">
                {option.flag}
              </span>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        className="flex-1"
        disabled={disabled}
        placeholder={placeholder}
        value={formatForDisplay(value, country)}
        onChange={(event) => onChange(toStorageValue(event.target.value, country))}
        {...formControlProps}
      />
    </div>
  );
}
